import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subject, firstValueFrom, filter, take } from 'rxjs';
import { PoiTypeSettingsService } from './poi-type-settings.service';
import { MiddlewareApiService } from '../api/middleware-api.service';

// Interface for site settings with defect POI type IDs
export interface DefectSiteSettings {
  defect1PoiTypeId: number;
  defect2PoiTypeId: number;
  defect3PoiTypeId: number;
  outdoorDefectPoiTypeId: number;
}

@Injectable({
  providedIn: 'root',
})
export class DefectAgendaService {
  private poiTypeSettingsService = inject(PoiTypeSettingsService);
  private middlewareApi = inject(MiddlewareApiService);
  private defectAgendaEnabledSubject = new BehaviorSubject<boolean>(false); // Disabled by default

  readonly defectAgendaEnabled$: Observable<boolean> = this.defectAgendaEnabledSubject.asObservable();

  // Dialog state for showing the "Enable defect agenda" confirmation modal
  private showDialogSubject = new Subject<void>();
  private dialogResultSubject = new Subject<boolean>();

  readonly showDialog$: Observable<void> = this.showDialogSubject.asObservable();
  readonly dialogResult$: Observable<boolean> = this.dialogResultSubject.asObservable();

  private currentSiteSettings: DefectSiteSettings | null = null;

  /**
   * Get current defect agenda state.
   */
  get defectAgendaEnabled(): boolean {
    return this.defectAgendaEnabledSubject.value;
  }

  /**
   * Set the current site settings (needed to know defect POI type IDs).
   */
  setSiteSettings(settings: DefectSiteSettings): void {
    this.currentSiteSettings = settings;
  }

  /**
   * Initialize the defect agenda for a site on app startup: load the site's defect
   * POI type IDs from middleware settings, then disable the defect agenda by default
   * so defect POIs are hidden until the user enables them.
   *
   * Mirrors the legacy app's AppRootController.$onInit() -> disableDefectAgenda().
   * Must be called after the IVION site is loaded (so hidePoiType has an effect).
   * Never throws: a settings-fetch failure is logged and ignored so map load is not blocked.
   */
  async initializeForSite(navvisSiteId: number): Promise<void> {
    try {
      const settings = await firstValueFrom(this.middlewareApi.getSettings());
      const siteInfo = settings.siteInfos?.find((s) => s.navvisSiteId === navvisSiteId);

      if (!siteInfo?.settings) {
        console.warn('[DefectAgenda] No site settings found for site', navvisSiteId);
        return;
      }

      this.setSiteSettings(siteInfo.settings);
      this.disableDefectAgenda();
    } catch (error) {
      console.error('[DefectAgenda] Failed to initialize defect agenda for site:', error);
    }
  }

  /**
   * Get all defect POI type IDs for the current site.
   */
  getDefectPoiTypeIds(): number[] {
    if (!this.currentSiteSettings) {
      return [];
    }

    return [
      this.currentSiteSettings.defect1PoiTypeId,
      this.currentSiteSettings.defect2PoiTypeId,
      this.currentSiteSettings.defect3PoiTypeId,
      this.currentSiteSettings.outdoorDefectPoiTypeId,
    ].filter((id) => id > 0);
  }

  /**
   * Enable defect agenda - show all defect POI types and add CSS class.
   */
  enableDefectAgenda(showAllDefectPoiTypes = true): void {
    document.body.classList.remove('disable-defect-agenda');

    if (showAllDefectPoiTypes) {
      const defectTypeIds = this.getDefectPoiTypeIds();
      for (const id of defectTypeIds) {
        this.poiTypeSettingsService.showPoiType(id);
      }
    }

    this.defectAgendaEnabledSubject.next(true);
  }

  /**
   * Disable defect agenda - hide all defect POI types and add CSS class.
   */
  disableDefectAgenda(): void {
    document.body.classList.add('disable-defect-agenda');

    const defectTypeIds = this.getDefectPoiTypeIds();
    for (const id of defectTypeIds) {
      this.poiTypeSettingsService.hidePoiType(id);
    }

    this.defectAgendaEnabledSubject.next(false);
  }

  /**
   * Check if all defect POI types are hidden and update the agenda state accordingly.
   * Called after toggling individual POI types in settings.
   */
  checkAndUpdateDefectAgenda(): void {
    const defectTypeIds = this.getDefectPoiTypeIds();
    if (defectTypeIds.length === 0) {
      return;
    }

    const allHidden = this.poiTypeSettingsService.areAllHidden(defectTypeIds);

    if (allHidden && this.defectAgendaEnabled) {
      // All defect types hidden -> disable agenda
      document.body.classList.add('disable-defect-agenda');
      this.defectAgendaEnabledSubject.next(false);
    } else if (!allHidden && !this.defectAgendaEnabled) {
      // At least one defect type visible -> enable agenda
      document.body.classList.remove('disable-defect-agenda');
      this.defectAgendaEnabledSubject.next(true);
    }
  }

  /**
   * Check if a given POI type ID is a defect type.
   */
  isDefectPoiType(poiTypeId: number): boolean {
    return this.getDefectPoiTypeIds().includes(poiTypeId);
  }

  /**
   * Check if defect agenda is enabled. If not, show a confirmation dialog.
   * Returns a Promise that resolves if the agenda is enabled (or user enables it),
   * and rejects if the user cancels.
   */
  async checkDefectAgenda(): Promise<void> {
    if (this.defectAgendaEnabled) {
      return Promise.resolve();
    }

    // Show the dialog
    this.showDialogSubject.next();

    // Wait for user response
    const result = await firstValueFrom(this.dialogResult$.pipe(take(1)));

    if (result) {
      this.enableDefectAgenda();
      return Promise.resolve();
    } else {
      return Promise.reject('cancelled');
    }
  }

  /**
   * Handle the dialog result from the UI component.
   */
  handleDialogResult(enabled: boolean): void {
    this.dialogResultSubject.next(enabled);
  }
}
